"use client"

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import { useFormBranchContext } from "@/features/auth/hooks/use-form-branch-context"
import {
  approvePledge,
  cancelPledge,
  createPledge,
  declinePledge,
  getPledgeDetail,
  listPledgeCampaigns,
  listPledges,
  recordInKindFulfillment,
  type ListPledgesTableQuery,
} from "@/features/pledges/services/pledges-api"
import {
  PledgeListView,
  type CancelPledgeInput,
  type CreatePledgeInput,
  type RecordInKindFulfillmentInput,
} from "@/features/pledges/model/pledge-types"

const PLEDGES_QUERY_KEY = ["pledges"] as const

function invalidatePledges(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({ queryKey: PLEDGES_QUERY_KEY })
}

export function usePledgesQuery(query: ListPledgesTableQuery | null) {
  const { activeBranchId } = useFormBranchContext()
  const resolvedBranchId = query?.branchId ?? activeBranchId ?? undefined

  return useQuery({
    queryKey: [...PLEDGES_QUERY_KEY, "list", query, resolvedBranchId ?? "__branch__"],
    queryFn: () => listPledges({ ...(query as ListPledgesTableQuery), branchId: resolvedBranchId }),
    enabled: Boolean(query?.workspaceId),
  })
}

/** The campaign cards and tab counts. Under the pledges key, so every pledge mutation refreshes them too. */
export function usePledgeCampaignsQuery(
  query: { workspaceId: string; branchId: string; view: PledgeListView } | null,
) {
  return useQuery({
    queryKey: [...PLEDGES_QUERY_KEY, "campaigns", query?.workspaceId, query?.branchId, query?.view],
    queryFn: () => listPledgeCampaigns(query as { workspaceId: string; branchId: string; view: PledgeListView }),
    enabled: Boolean(query?.workspaceId && query?.branchId),
  })
}

export function usePledgeDetailQuery(id: string | null) {
  return useQuery({
    queryKey: [...PLEDGES_QUERY_KEY, "detail", id],
    queryFn: () => getPledgeDetail(id as string),
    enabled: Boolean(id),
  })
}

export function useCreatePledgeMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: CreatePledgeInput) => createPledge(input),
    onSuccess: () => invalidatePledges(queryClient),
  })
}

export function useCancelPledgeMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: CancelPledgeInput) => cancelPledge(input),
    onSuccess: () => invalidatePledges(queryClient),
  })
}

export function useApprovePledgeMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => approvePledge(id),
    onSuccess: () => invalidatePledges(queryClient),
  })
}

export function useDeclinePledgeMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: { id: string; reason?: string | null }) => declinePledge(input),
    onSuccess: () => invalidatePledges(queryClient),
  })
}

export function useRecordInKindFulfillmentMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: RecordInKindFulfillmentInput) => recordInKindFulfillment(input),
    onSuccess: () => invalidatePledges(queryClient),
  })
}
